import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AddChannelModal from "../modals/AddChannelModal";
import ChannelAddedModal from "../modals/ChannelAddelModal";
import ChannelPendingModal from "../modals/ChannelPendingModal";
import AddToSlackModal from "../modals/AddUserToSlack";
import AddRecordingFlowWrapper from "../modals/AddRecordingFlowWrapper";

const RouteConfigModalsContext = createContext(null);

const getChannelsFromStorage = () =>
  JSON.parse(localStorage.getItem("organization"))?.channels?.L || [];
const getRecordingsFromStorage = () =>
  JSON.parse(localStorage.getItem("organization"))?.recordings?.L || [];

export function RouteConfigModalsProvider({ children, planDetails = null }) {
  const [channels, setChannelsState] = useState(getChannelsFromStorage);
  const [recordings, setRecordingsState] = useState(getRecordingsFromStorage);

  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [addChannelInitialType, setAddChannelInitialType] = useState(null);
  const [addRecordingOpen, setAddRecordingOpen] = useState(false);
  const onRecordingAddedRef = useRef(null);
  const onChannelAddedRef = useRef(null);

  const [openChannelAddedModal, setOpenChannelAddedModal] = useState(false);
  const [openChannelPendingModal, setOpenChannelPendingModal] = useState(false);
  const [addToSlackModal, setOpenAddToSlackModal] = useState(false);
  const [channelId, setChannelId] = useState(null);

  const setChannels = useCallback((next) => {
    setChannelsState(next);
    const org = JSON.parse(localStorage.getItem("organization"));
    if (org) {
      org.channels = { L: next };
      localStorage.setItem("organization", JSON.stringify(org));
    }
  }, []);

  const setRecordings = useCallback((next) => {
    setRecordingsState(next);
    const org = JSON.parse(localStorage.getItem("organization"));
    if (org) {
      org.recordings = { L: next };
      localStorage.setItem("organization", JSON.stringify(org));
    }
  }, []);

  const refreshChannels = useCallback(() => {
    setChannelsState(getChannelsFromStorage());
  }, []);

  const refreshRecordings = useCallback(() => {
    setRecordingsState(getRecordingsFromStorage());
  }, []);

  const openAddChannelModal = useCallback((initialType = null, onChannelAdded) => {
    setAddChannelInitialType(initialType);
    onChannelAddedRef.current = onChannelAdded || null;
    refreshChannels();
    setAddChannelOpen(true);
  }, [refreshChannels]);

  const callChannelAddedAndClear = useCallback((newChannelValue) => {
    if (onChannelAddedRef.current) {
      onChannelAddedRef.current(newChannelValue);
      onChannelAddedRef.current = null;
    }
    refreshChannels();
    if (typeof window !== "undefined" && window.__twilanswerAssistantPush) {
      window.__twilanswerAssistantPush({
        type: "channel_added",
        channelValue: newChannelValue,
      });
    }
  }, [refreshChannels]);

  const openAddRecordingModal = useCallback((onRecordingAdded) => {
    onRecordingAddedRef.current = onRecordingAdded || null;
    refreshRecordings();
    setAddRecordingOpen(true);
  }, [refreshRecordings]);

  const callRecordingAddedAndClear = useCallback((newRecId) => {
    if (onRecordingAddedRef.current) {
      onRecordingAddedRef.current(newRecId);
      onRecordingAddedRef.current = null;
    }
    refreshRecordings();
    // Notify onboarding assistant if a session is listening
    if (typeof window !== "undefined" && window.__twilanswerAssistantPush) {
      window.__twilanswerAssistantPush({
        type: "recording_added",
        recordingId: newRecId,
      });
    }
  }, [refreshRecordings]);

  // Stay in sync when recordings/channels are added via the assistant bridge modal
  useEffect(() => {
    const onOrgAssetsChanged = () => {
      refreshRecordings();
      refreshChannels();
    };
    window.addEventListener("twilanswer-org-assets-changed", onOrgAssetsChanged);
    return () => {
      window.removeEventListener("twilanswer-org-assets-changed", onOrgAssetsChanged);
    };
  }, [refreshRecordings, refreshChannels]);

  const value = useMemo(
    () => ({
      channels,
      setChannels,
      recordings,
      setRecordings,
      refreshChannels,
      refreshRecordings,
      openAddChannelModal,
      openAddRecordingModal,
      planDetails,
    }),
    [
      channels,
      setChannels,
      recordings,
      setRecordings,
      refreshChannels,
      refreshRecordings,
      openAddChannelModal,
      openAddRecordingModal,
      planDetails,
    ]
  );

  return (
    <RouteConfigModalsContext.Provider value={value}>
      {children}
      <ChannelAddedModal
        setChannels={setChannels}
        open={openChannelAddedModal}
        setOpen={setOpenChannelAddedModal}
      />
      <ChannelPendingModal
        open={openChannelPendingModal}
        setOpen={setOpenChannelPendingModal}
      />
      <AddToSlackModal
        open={addToSlackModal}
        setOpen={setOpenAddToSlackModal}
        channel_id={channelId}
        setChannelId={setChannelId}
      />
      <AddChannelModal
        open={addChannelOpen}
        setOpen={setAddChannelOpen}
        channels={channels}
        setChannels={setChannels}
        channelAddedModal={openChannelAddedModal}
        setOpenChannelAddedModal={setOpenChannelAddedModal}
        channelPendingModal={openChannelPendingModal}
        setOpenChannelPendingModal={setOpenChannelPendingModal}
        addToSlackModal={addToSlackModal}
        setOpenAddToSlackModal={setOpenAddToSlackModal}
        setChannelId={setChannelId}
        initialChannelType={addChannelInitialType}
        callChannelAddedAndClear={callChannelAddedAndClear}
      />
      <AddRecordingFlowWrapper
        open={addRecordingOpen}
        setOpen={setAddRecordingOpen}
        setRecordings={setRecordings}
        recordings={recordings}
        callRecordingAddedAndClear={callRecordingAddedAndClear}
      />
    </RouteConfigModalsContext.Provider>
  );
}

export function useRouteConfigModals() {
  return useContext(RouteConfigModalsContext);
}
